import { motion } from 'framer-motion';
import {
    ArrowRight,
    CheckCircle,
    Key,
    Shield,
    Zap
} from 'lucide-react';
import { Link } from 'react-router-dom';

const IssueCredentials = () => {
    return (
        <div className="min-h-screen pt-20">
            {/* Hero Section */}
            <section className="py-20 bg-surface-100">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                            Issue Verifiable Credentials
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Create tamper-proof, instantly verifiable digital credentials that work across all major wallet platforms and verification systems worldwide.
                        </p>
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 className="text-2xl font-bold text-white mb-6">
                                Enterprise-Grade Credential Issuance
                            </h2>
                            <div className="space-y-4">
                                <div className="flex items-start space-x-4">
                                    <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <Shield className="w-5 h-5 text-blue-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">W3C Compliant Standards</h3>
                                        <p className="text-gray-300 text-sm">Built on W3C Verifiable Credentials specification for maximum interoperability</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-4">
                                    <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <Zap className="w-5 h-5 text-green-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Instant Issuance</h3>
                                        <p className="text-gray-300 text-sm">Issue credentials in seconds via API, web dashboard, or bulk upload</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-4">
                                    <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <Key className="w-5 h-5 text-purple-400" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white mb-1">Cryptographic Security</h3>
                                        <p className="text-gray-300 text-sm">Digital signatures with blockchain anchoring for tamper-proof verification</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="glass rounded-2xl p-8 border border-white/20">
                                <h3 className="text-lg font-bold text-white mb-6">Credential Template</h3>
                                <div className="space-y-4">
                                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                                        <div className="text-blue-400 text-sm font-medium mb-2">University Degree</div>
                                        <div className="text-white text-sm">Bachelor of Computer Science</div>
                                        <div className="text-gray-400 text-xs mt-1">Issued: 2024 • Valid: Permanent</div>
                                    </div>
                                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                                        <div className="text-green-400 text-sm font-medium mb-2">Professional License</div>
                                        <div className="text-white text-sm">Software Engineering License</div>
                                        <div className="text-gray-400 text-xs mt-1">Expires: 2026 • Renewable</div>
                                    </div>
                                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                                        <div className="text-purple-400 text-sm font-medium mb-2">Employment Certificate</div>
                                        <div className="text-white text-sm">Senior Developer Position</div>
                                        <div className="text-gray-400 text-xs mt-1">Duration: 2020-2024</div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-gradient-to-r from-primary-600 to-neon-600">
                <div className="container-custom text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                            Ready to Start Issuing Credentials?
                        </h2>
                        <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                            Join the digital credentials revolution and create verifiable credentials that work everywhere.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href="https://app.passhub.documint.id/signup" target="_blank" rel="noopener noreferrer" className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center">
                                Start Free Trial
                                <ArrowRight size={20} className="ml-2" />
                            </a>
                            <Link to="/contact" className="border border-white/20 text-white hover:bg-white/10 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center">
                                Contact Sales
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default IssueCredentials;